import Backbone from 'backbone';

import {Part} from 'BioJS';
import {Sequence} from 'components/Sequence';
import {RED_POWDER} from 'ui-library/Colors';

export interface PartHighlightRegion {
  start: number;
  end: number;
  // name and isArchived are only used for rendering old-style non-overlapping parts with the text
  // on top.
  name: string;
  isArchived: boolean;
  baseFillColor: string;
  isOverlap: boolean;
  outOfSync: boolean;
  // The partRef is null if the region represents an overlapping region that cannot point to a single part.
  // Needed to show the bioentity popover on part labels for non-overlapping parts,
  // but can be removed once PARTS_ENABLE_OVERLAPPING_PARTS flag is removed.
  partRef: Part | null;
}

interface PartPoint {
  index: number;
  isEnd: boolean;
  partRef: Part;
}

// A ViewPart is a "fake" part just for the purposes of presenting the correct interface to methods in
// RingView that expect a backbone model, and are too complicated to completely refactor at this time.
interface ViewPartAttributes extends PartHighlightRegion {
  annularLayer?: 0;
  forceLabel?: boolean;
  hasCircularLabel?: boolean;
}

export interface ViewPart extends Backbone.Model {
  get<K extends keyof ViewPartAttributes>(key: K): ViewPartAttributes[K];
  set(obj: Partial<ViewPartAttributes>, options?: {silent?: boolean}): ViewPart;
  set<K extends keyof ViewPartAttributes>(key: K, value: ViewPartAttributes[K]): ViewPart;
}

export class ViewPart extends Backbone.Model {
  start(): number {
    return this.get('start');
  }

  end(): number {
    return this.get('end');
  }

  color(): string {
    return this.get('outOfSync') ? RED_POWDER : this.get('baseFillColor');
  }

  hover(): boolean {
    return false;
  }

  selected(): boolean {
    return false;
  }

  displayLabel(): string {
    return this.get('name');
  }

  uid(): string {
    return this.cid;
  }

  partRef(): Part | null {
    return this.get('partRef');
  }
}

export function makeViewParts(partRegions: Array<PartHighlightRegion>): Array<ViewPart> {
  return partRegions.map((pr) => new ViewPart(pr));
}

function adjustEnd(end: number, seqLength: number): number {
  return end === 0 ? seqLength : end;
}

function isCrossOrigin(part: Part): boolean {
  return part.end() !== 0 && part.start() >= part.end();
}

function linearizeParts(parts: Array<Part>): Array<{start: number; end: number; part: Part}> {
  return parts.flatMap((p) =>
    isCrossOrigin(p)
      ? [
          {start: p.start(), end: 0, part: p},
          {
            start: 0,
            end: p.end(),
            part: p,
          },
        ]
      : [{start: p.start(), end: p.end(), part: p}]
  );
}

/**
 * This function translates a set of parts, which may or may not overlap, into a set of smaller regions that
 * do not overlap. Each region represents a section of the sequence where either only one part exists, or one
 * or more parts exist. This allows us to render overlapping and non-overlapping sections differently.
 * For example, given these 4 parts:
 *
 * 0      7     13   18   23      31  35      44
 * {------p1---------}    {----p3-----}
 *        {-p2--}                 {-----p4-----}
 * GATTACAGATTACAGATTACAGATTACAGATTACAGATTACAGATTACAGATTACA
 *
 * it derives 6 regions (ol = isOverlap):
 *
 * ol=f   ol=t  ol=f      ol=f    ol=t ol=f
 * {-r1--}{-r2-}{-r3-}    {-r4---}{r5}{-r6-----}
 * GATTACAGATTACAGATTACAGATTACAGATTACAGATTACAGATTACAGATTACA
 *
 * The algorithm works like this:
 *  - It splits parts that cross the origin into two parts that do not cross the origin.
 *  - Then, it sorts all the start/end points for the parts. E.g. [0s, 7s, 13e, 18e, 23s, 31s, 35e, 44e].
 *  - Finally, it iterates through the points, keeping track of the number of overlapping parts in each region
 *    between points by counting the start/end balance. It makes regions between consecutive points, and
 *    assigns them overlap status, depending on this part count.
 *
 * @param parts
 * @param containerSequence
 */
export default function splitPartsIntoHighlightRegions(
  parts: Array<Part>,
  containerSequence: Sequence
): Array<PartHighlightRegion> {
  function pointCompare(point1: PartPoint, point2: PartPoint): number {
    const endComp =
      point1.partRef.id === point2.partRef.id
        ? Number(point1.isEnd) - Number(point2.isEnd)
        : Number(point2.isEnd) - Number(point1.isEnd);
    return point1.index === point2.index ? endComp : point1.index - point2.index;
  }

  if (!parts.length) {
    return [];
  }
  const highlightRegions: Array<PartHighlightRegion> = [];
  const points: Array<PartPoint> = linearizeParts(parts).flatMap((p) => [
    {index: p.start, isEnd: false, partRef: p.part},
    {index: adjustEnd(p.end, containerSequence.length()), isEnd: true, partRef: p.part},
  ]);
  points.sort((p1, p2) => pointCompare(p1, p2));
  const curParts: {[id: string]: Part} = {[points[0].partRef.id]: points[0].partRef};
  for (const [prevIndex, point] of points.slice(1).entries()) {
    const prevPoint = points[prevIndex];
    const nparts = Object.values(curParts).length;
    if (point.index > prevPoint.index && nparts > 0) {
      highlightRegions.push({
        start: prevPoint.index,
        end: point.index === containerSequence.length() ? 0 : point.index,
        // name and isArchived are only used for rendering old-style non-overlapping parts with the text
        // on top.
        name: point.partRef.name(),
        isArchived: point.partRef.sequence().isArchived(),
        // Would like to remove baseFillColor and use a constant for all, but for tenants where we haven't
        // enabled overlapping parts yet the fill color indicates out-of-syncness.
        baseFillColor: point.partRef.color(),
        isOverlap: nparts > 1,
        outOfSync: Object.values(curParts).some((p) => !p.isInSync(containerSequence._sequence())),
        partRef: nparts > 1 ? null : Object.values(curParts)[0],
      });
    }
    // Avoids deleting part from curParts if region represents a cross-origin part that starts and ends at the
    // same position, fixing bug where latter region of a cross-origin part with the same start and end
    // position was unhighlighted.
    if (point.isEnd && !(prevPoint.index === point.index && prevPoint.partRef === point.partRef)) {
      delete curParts[point.partRef.id];
    } else {
      curParts[point.partRef.id] = point.partRef;
    }
  }

  return highlightRegions;
}
